import { useCallback, useState } from 'react'
import { createContainer } from 'unstated-next'

import { EmbroideryTypeEnum } from '../../__generated__/graphql/catalog/graphql'

import { EmbroideryService } from './EmbroideryService'
import { useEmbroideryConfirmation } from './useEmbroideryConfirmation'

export interface UseEmbroideryModalProps {
  onOpenEmbroideryModal?: () => void
  /** The discount messaging to display at the bottom of the embroidery modal. */
  discountMessage?: string
  /** Whether or not prices should be displayed within the embroidery modal.*/
  withPricing?: boolean
  /** Embroidery form options that should be considered "required" within the embroidery modal. */
  requiredEmbroideryOptions?: EmbroideryTypeEnum[]
}

type OpenOptions = {
  triggerElement?: HTMLElement | null
}

function useEmbroideryModal({
  onOpenEmbroideryModal,
  discountMessage,
  withPricing = true,
  requiredEmbroideryOptions = [],
}: UseEmbroideryModalProps | undefined = {}) {
  const { setSelectedFormKind } = EmbroideryService.useContainer()
  const [shouldDisplayEmbroideryModal, setShouldDisplayEmbroideryModal] = useState<boolean>(false)
  const { setConfirmAddEmbroidery } = EmbroideryService.useContainer()
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null)

  const openEmbroideryModal = useCallback(
    (options?: OpenOptions) => {
      setTriggerElement(options?.triggerElement ?? null)
      setShouldDisplayEmbroideryModal(true)
      onOpenEmbroideryModal && onOpenEmbroideryModal()
    },
    [onOpenEmbroideryModal]
  )

  const closeEmbroideryModal = useCallback(() => {
    setSelectedFormKind(null)
    setShouldDisplayEmbroideryModal(false)
    setTriggerElement(null)
  }, [setSelectedFormKind, setShouldDisplayEmbroideryModal])

  const { handleConfirm, formErrors } = useEmbroideryConfirmation({
    requiredEmbroideryOptions,
    onSubmit: () => {
      setConfirmAddEmbroidery(true)
      closeEmbroideryModal()
    },
  })

  return {
    closeEmbroideryModal,
    openEmbroideryModal,
    shouldDisplayEmbroideryModal,
    triggerElement,
    handleConfirm,
    formErrors,
    discountMessage,
    withPricing,
    requiredEmbroideryOptions,
  }
}

export const EmbroideryModalService = createContainer(useEmbroideryModal)
